'use client';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useBunja } from 'bunja/react';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';

import {
  getExchangeRatesApi,
  getLuckyDrawApi,
  postLuckyDrawApi,
  postRegisterLuckyDrawApi,
  putLuckyDrawTokensApi,
} from '@flipster/apis/v1/campaign';
import { abTestBunja } from '@flipster/features/src/shared/ab-test';
import { useAuth } from '@flipster/features/src/shared/auth/state';
import { snackbar } from '@flipster/react/design-system/snackbar/index';
import { ServerError } from '@flipster/utils/constants/server-error';

import { authRedirectBunja } from '@web/components/AuthRedirect/bunja';

import { slugIds } from './constants';

export function useLuckyDraw(id: string) {
  const { getPrexInstallationId } = useBunja(abTestBunja);
  const prexInstallationId = getPrexInstallationId();
  const { isCookieLoggedIn } = useAuth();

  return useQuery({
    queryKey: ['getLuckyDrawApi', id, prexInstallationId, isCookieLoggedIn],
    queryFn: () => getLuckyDrawApi({ eventId: id, installId: prexInstallationId }),
    select: (data) => ({ id, ...data.data }),
  });
}

export function useRegisterLuckyDraw() {
  const router = useRouter();
  const { setReferrerPath } = useBunja(authRedirectBunja);
  const t = useTranslations();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['postRegisterLuckyDrawApi'],
    mutationFn: () => postRegisterLuckyDrawApi({ slugIds }),
    onSuccess: () => {
      snackbar.success({ message: 'You are registered to the event' });
      queryClient.invalidateQueries({ queryKey: ['getLuckyDrawApi'] });
    },
    onError: (error) => {
      if (!isAxiosError(error)) return;
      switch (error.response?.status) {
        case 401:
          setReferrerPath();
          snackbar.error({ message: t('Common.r250918.Please_log_in') });
          router.push('/signup');
          break;
        default:
          snackbar.error({ message: ServerError['api.server.error'] });
          break;
      }
    },
  });
}

export function useSpinLuckyDraw(id: string) {
  const { getPrexInstallationId } = useBunja(abTestBunja);
  const { setReferrerPath } = useBunja(authRedirectBunja);

  const { isCookieLoggedIn } = useAuth();
  const router = useRouter();
  const t = useTranslations();
  const queryClient = useQueryClient();

  const prexInstallationId = getPrexInstallationId();

  return useMutation({
    mutationKey: ['postLuckyDrawApi'],
    mutationFn: () => postLuckyDrawApi({ eventId: id, installId: prexInstallationId }),
    onError: (error) => {
      if (!isAxiosError(error)) return;
      switch (error.response?.status) {
        case 401:
          setReferrerPath();
          snackbar.error({ message: t('Common.r250918.Please_log_in') });
          router.push('/signup');
          break;
        case 403:
          snackbar.error({ message: 'Your sweets may have been used.\nPlease refresh and try again.' });
          queryClient.invalidateQueries({ queryKey: ['getLuckyDrawApi', id, prexInstallationId, isCookieLoggedIn] });
          break;
        default:
          snackbar.error({ message: ServerError['api.server.error'] });
          break;
      }
    },
  });
}

export function useGetExchangeRates() {
  return useQuery({
    queryKey: ['getExchangeRatesApi'],
    queryFn: () => getExchangeRatesApi({ eventId: 'spooky-treats-festival' }),
    select: (data) => data.data,
  });
}

interface ExchangeTokensProps {
  from: { id: string; tokens: number };
  to: { id: string; tokens: number };
}

export function useExchangeTokens() {
  const { setReferrerPath } = useBunja(authRedirectBunja);

  const router = useRouter();
  const t = useTranslations();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['putLuckyDrawTokensApi'],
    mutationFn: ({ from, to }: ExchangeTokensProps) => putLuckyDrawTokensApi({ from, to }),
    onSuccess: () => {
      snackbar.success({ message: 'Your sweets are exchanged' });
      queryClient.invalidateQueries({ queryKey: ['getLuckyDrawApi'] });
    },
    onError: (error) => {
      if (!isAxiosError(error)) return;
      switch (error.response?.status) {
        case 401:
          setReferrerPath();
          snackbar.error({ message: t('Common.r250918.Please_log_in') });
          router.push('/signup');
          break;
        case 403:
          snackbar.error({ message: 'Your sweets may have been used.\nPlease refresh and try again.' });
          queryClient.invalidateQueries({ queryKey: ['getLuckyDrawApi'] });
          break;
        default:
          snackbar.error({ message: ServerError['api.server.error'] });
          break;
      }
    },
  });
}

export const useSpookyTreatsFestivalLuckyDraw = () => {
  const { data: luckyDrawData1 } = useLuckyDraw('spooky-treats-festival-lv1');
  const { data: luckyDrawData2 } = useLuckyDraw('spooky-treats-festival-lv2');
  const { data: luckyDrawData3 } = useLuckyDraw('spooky-treats-festival-lv3');

  const luckyDrawData = [luckyDrawData1, luckyDrawData2, luckyDrawData3];
  const isRegistered = luckyDrawData.every((luckyDraw) => luckyDraw != null && luckyDraw.isRegistered);
  const status = luckyDrawData1?.luckyDrawEventStatus;
  const rewards = luckyDrawData.flatMap((luckyDraw) => luckyDraw?.prizes ?? []);

  const { mutateAsync: registerLuckyDraw } = useRegisterLuckyDraw();

  const { data: exchangeCosts } = useGetExchangeRates();
  const getExchangeCost = (fromSlug: string, toSlug: string) => {
    return exchangeCosts?.pairs.find((cost) => cost.from === fromSlug && cost.to === toSlug);
  };

  return {
    luckyDrawData,
    rewards,
    isRegistered,
    registerLuckyDraw,
    getExchangeCost,
    status,
  };
};

export default useLuckyDraw;
